/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDialogContentStyleProps, IDialogContentStyles } from '@fluentui/react/lib/Dialog';
import { IStackItemProps, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logNextWizardStep } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { AutotagTriggerModalStepsNavigator } from 'src/customText/verticals/autotagTriggerModal/components/AutotagTriggerModalStepsNavigator';
import { ReviewAndFinishScreen } from 'src/customText/verticals/autotagTriggerModal/components/ReviewAndFinishScreen';
import { SelectEntitiesScreen } from 'src/customText/verticals/autotagTriggerModal/components/SelectEntitiesScreen';
import { SelectFilesScreen } from 'src/customText/verticals/autotagTriggerModal/components/SelectFilesScreen';
import { SelectTrainedModelScreen } from 'src/customText/verticals/autotagTriggerModal/components/SelectTrainedModelScreen';
import { AutoLabelingInfoDto, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';

const messages = defineMessages({
    modalTitle: {
        id: 'customText.autotagTriggerModal.modalTitle',
        defaultMessage: 'Trigger Auto-label'
    }
});

export const AutotagTriggerContentLeftMargin = '32px';

const { styleComponent } = getStylistV2('AutotagTriggernModalBase');
const modalTheme = getScopedTheme('ProjectCreationModalBase');
const borderStyle = `solid 1px ${modalTheme.borderColor}`;
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    inner: { paddingTop: '0px', paddingBottom: '0px', borderTop: borderStyle }
});
const ContentWrapper = styleComponent<IStackProps>(Stack)('ContentWrapper', () => ({
    paddingTop: '28px',
    width: '100%',
    minHeight: '600px',
    color: modalTheme.defaultTextColor,
    minWidth: 0,
    position: 'relative'
}));
const NavigatorWrapper = styleComponent<IStackItemProps>(Stack.Item)(
    'NavigatorWrapper',
    (_, cssVars) => ({
        width: cssVars.width,
        borderRight: cssVars.borderRight,
        paddingTop: '34px'
    }),
    {
        cssVars: { width: '', borderRight: '' },
        cssVarsSetter: ({ horizontal }: { horizontal: boolean }) => ({
            width: horizontal ? '300px' : '',
            borderRight: horizontal ? borderStyle : ''
        })
    }
);

const getInitialAutoTagtDto = (): AutoLabelingInfoDto => ({
    documents: [],
    selectedChars: 0,
    classesNames: [],
    entitiesNames: [],
    trainedModelLabel: null,
    azureOpenAiDeploymentName: '',
    azureOpenAiResourceRegion: '',
    autoLabelingJobType: 'trainedModel'
});

/**
 * Represents the customText autotag trigger modal.
 */
export const AutotagTriggerModal = withModalMounter(
    observer((props: BasicModalProps<object>) => {
        const { onDismiss, onSubmit } = props;
        const { localize } = useLocalization();
        const { serviceName } = getPortalFromUrl();
        const { isLargeOrGreater } = useResponsiveMode();

        const [autotagInfoDto, updateAutotagInfoDto] = useUpdateState<AutoLabelingInfoDto>(getInitialAutoTagtDto);
        const [activeStep, setActiveStep] = React.useState(AutoLabelingSteps.ConfigureJob);

        const handleStepSubmissions = (dto: Partial<AutoLabelingInfoDto>) => {
            updateAutotagInfoDto(dto);
            setActiveStep(activeStep + 1);
            logNextWizardStep(serviceName, 'AutoLabel', AutoLabelingSteps[activeStep], '');
        };

        const handleReviewAndFinishStep = () => {
            logNextWizardStep(serviceName, 'AutoLabel', 'ReviewAndFinish', '');
            onSubmit(autotagInfoDto);
        };

        const handleDismissStep = () => {
            logNextWizardStep(serviceName, 'AutoLabel', AutoLabelingSteps[activeStep], 'Cancel');
            onDismiss();
        };

        const getStepContent = () => {
            switch (activeStep) {
                case AutoLabelingSteps.ConfigureJob:
                    return (
                        <SelectTrainedModelScreen
                            onDismiss={handleDismissStep}
                            autotagInfoDto={autotagInfoDto}
                            onSubmit={trainedModelLabel => handleStepSubmissions({ trainedModelLabel })}
                        />
                    );
                case AutoLabelingSteps.SelectEntitiesOrClasses:
                    return (
                        <SelectEntitiesScreen
                            onDismiss={handleDismissStep}
                            autotagInfoDto={autotagInfoDto}
                            onSubmit={entitiesNames => handleStepSubmissions({ entitiesNames })}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                        />
                    );
                case AutoLabelingSteps.SelectFiles:
                    return (
                        <SelectFilesScreen
                            onDismiss={handleDismissStep}
                            autotagInfoDto={autotagInfoDto}
                            onSubmit={(documents, selectedChars) => handleStepSubmissions({ documents, selectedChars })}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                        />
                    );

                case AutoLabelingSteps.ReviewAndFinish:
                    return (
                        <ReviewAndFinishScreen
                            onDismiss={handleDismissStep}
                            autotagInfoDto={autotagInfoDto}
                            onSubmit={handleReviewAndFinishStep}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                        />
                    );

                default:
            }
        };

        return (
            <BasicModal
                {...props}
                minWidth={1200}
                shouldRenderFooter={false}
                dialogContentProps={{ title: localize(messages.modalTitle), styles: dialogContentStyles }}>
                <Stack horizontal={isLargeOrGreater} verticalFill>
                    <NavigatorWrapper customProps={{ horizontal: isLargeOrGreater }}>
                        <AutotagTriggerModalStepsNavigator
                            activeStep={activeStep}
                            horizontal={!isLargeOrGreater}
                            onActiveStepChange={setActiveStep}
                        />
                    </NavigatorWrapper>

                    <ContentWrapper grow verticalFill>
                        {getStepContent()}
                    </ContentWrapper>
                </Stack>
            </BasicModal>
        );
    })
);
